import type { RegisteredComponent } from "@builder.io/sdk-react";

import { AccordionComponent } from "@/components/Accordion/Accordion.builder";
import { AccordionItemComponent } from "@/components/AccordionItem/AccordionItem.builder";
import { AccountDashboardBannerComponent } from "@/components/AccountDashboardBanner";
import { AdditionalBenefitsComponent } from "@/components/AdditionalBenefits";
import { AnchorLinkComponent } from "@/components/AnchorLink/AnchorLink.builder";
import { AnchorPushComponent } from "@/components/AnchorPush";
import { ArticleRestrictedContentComponent } from "@/components/ArticleRestrictedContent";
import { BlogComponent } from "@/components/Blog/Blog.builder";
import { BlogArticleComponent } from "@/components/BlogArticle/BlogArticle.builder";
import { BlogArticleCarouselComponent } from "@/components/BlogArticleCarousel/BlogArticleCarousel.builder";
import { BuilderImageComponent } from "@/components/BuilderImage/BuilderImage.builder";
import { CallToActionComponent } from "@/components/CallToAction";
import { CartComponent } from "@/components/Cart";
import { CheckoutComponent } from "@/components/Checkout";
import { ColumnComponent } from "@/components/Column/Column.builder";
import { ComingSoonComponent } from "@/components/ComingSoon/ComingSoon.builder";
import { ConsentReminderDialogComponent } from "@/components/ConsentReminderDialog";
import { ContactBlockComponent } from "@/components/ContactBlock/ContactBlock.builder";
import { ContactFormComponent } from "@/components/ContactForm/ContactForm.builder";
import { ContainerComponent } from "@/components/Container/Container.builder";
import { ContentGateComponent } from "@/components/ContentGate";
import { CounterComponent } from "@/components/Counter/Counter.builder";
import { CountrySelectorComponent } from "@/components/CountrySelector/CountrySelector.builder";
import { CtaComponent } from "@/components/Cta/Cta.builder";
import { CtaBlockComponent } from "@/components/CtaBlock/CtaBlock.builder";
import { DiscountComponent } from "@/components/Discount/Discount.builder";
import { DiscreteHeaderBannerComponent } from "@/components/DiscreteHeaderBanner";
import { DoubleOptInRedirectComponent } from "@/components/DoubleOptInRedirect/DoubleOptInRedirect.builder";
import { DownloadButtonComponent } from "@/components/DownloadButton/DownloadButton.builder";
import { EarnPointsComponent } from "@/components/EarnPoints/EarnPoints.builder";
import { EcommerceReassuranceBannerComponent } from "@/components/EcommerceReassuranceBanner/EcommerceReassuranceBanner.builder";
import { EventsLocatorComponent } from "@/components/EventsLocator";
import { FaqComponent } from "@/components/Faq/Faq.builder";
import { FaqArticleComponent } from "@/components/FaqArticle/FaqArticle.builder";
import { FaqResultsContainerComponent } from "@/components/FaqResultsContainer/FaqResultsContainer.builder";
import { FlavorFinderComponent } from "@/components/FlavorFinder/FlavorFinder.builder";
import { FlavourCarouselComponent } from "@/components/FlavourCarousel/FlavourCarousel.builder";
import { FlavourCarouselCardComponent } from "@/components/FlavourCarouselCard/FlavourCarouselCard.builder";
import { FooterListComponent } from "@/components/Footer/components/FooterList/FooterList.builder";
import { HealthBannerComponent } from "@/components/Footer/components/HealthBanner/HealthBanner.builder";
import { FormCheckboxComponent } from "@/components/FormCheckbox/FormCheckbox.builder";
import { FormConsentCheckboxListComponent } from "@/components/FormConsentCheckboxList/FormConsentCheckboxList.builder";
import { FormSelectComponent } from "@/components/FormSelect/FormSelect.builder";
import { FormTextAreaComponent } from "@/components/FormTextArea/FormTextArea.builder";
import { FormTextInputComponent } from "@/components/FormTextInput/FormTextInput.builder";
import { GenericLayoutTitleComponent } from "@/components/GenericLayoutTitle/GenericLayoutTitle.builder";
import { GridComponent } from "@/components/Grid/Grid.builder";
import { HeaderBlockComponent } from "@/components/HeaderBlock";
import { HeroBannerContainerComponent } from "@/components/HeroBannerContainer/HeroBannerContainer.builder";
import { HeroBannerWithCardsComponent } from "@/components/HeroBannerWithCards/HeroBannerWithCards.builder";
import { HeroContainerComponent } from "@/components/HeroContainer/HeroContainer.builder";
import { HeroComponent } from "@/components/HeroV2/HeroV2.builder";
import { HighlightCardComponent } from "@/components/HighlightCard/HighlightCard.builder";
import { HoReCaLocatorComponent } from "@/components/HoReCaLocator";
import { ImageTopicGalleryComponent } from "@/components/ImageGallery/ImageGallery.builder";
import { ImageTopicGalleryTopicComponent } from "@/components/ImageGalleryTopic/ImageGalleryTopic.builder";
import { ImageInfoBannerComponent } from "@/components/ImageInfoBanner";
import { ImageLinkComponent } from "@/components/ImageLink/ImageLink.builder";
import { InfoBanner } from "@/components/InfoBanner/InfoBanner.builder";
import { InformationAboutProductsComponent } from "@/components/InformationAboutProducts/InformationAboutProducts.builder";
import { LegalDisclaimerComponent } from "@/components/LegalDisclaimer";
import { LinksListComponent } from "@/components/LinksList/LinksList.builder";
import { LogoComponent } from "@/components/Logo";
import { LoyaltyContactFormComponent } from "@/components/LoyaltyContactForm/LoyaltyContactForm.builder";
import { LoyaltyContactFormCheckboxSectionComponent } from "@/components/LoyaltyContactFormCheckboxSection/LoyaltyContactFormCheckboxSection.builder";
import { LoyaltyContactFormTextAreaComponent } from "@/components/LoyaltyContactFormTextArea/LoyaltyContactFormTextArea.builder";
import { LoyaltyHeroBannerContainerComponent } from "@/components/LoyaltyHeroBannerContainer/LoyaltyHeroBannerContainer.builder";
import { LoyaltyHeroComponent } from "@/components/LoyaltyHeroV2/LoyaltyHero.builder";
import { LoyaltyPointsStatusComponent } from "@/components/LoyaltyPointsStatus/LoyaltyPointsStatus.builder";
import { LoyaltyPushBlockComponent } from "@/components/LoyaltyPushBlock";
import { LoyaltyReferralCodeExtendedComponent } from "@/components/LoyaltyReferralCodeExtended/LoyaltyReferralCodeExtended.builder";
import { LoyaltyReferralStatsComponent } from "@/components/LoyaltyReferralStats/LoyaltyReferralStats.builder";
import { LoyaltySoftGateComponent } from "@/components/LoyaltySoftGate";
import { LoyaltyTabsComponent } from "@/components/LoyaltyTabs";
import { LoyaltyVideoPlayerComponent } from "@/components/LoyaltyVideoPlayer";
import { LoyaltyVideoPreviewComponent } from "@/components/LoyaltyVideoPreview";
import { LoyaltyWrapperComponent } from "@/components/LoyaltyWrapper";
import { MiscellaneousPageContainerComponent } from "@/components/MiscellaneousPageContainer/MiscellaneousPageContainer.builder";
import { NotFoundComponent } from "@/components/NotFound";
import { OnsitePersonalizationModalComponent } from "@/components/OnsitePersonalizationModal";
import { OverviewComponent } from "@/components/Overview/Overview.builder";
import { PackCodesComponent } from "@/components/PackCodes";
import { PersonalizationContainerComponent } from "@/components/PersonalizationContainer";
import { PointsAmountBannerComponent } from "@/components/PointsAmountBanner";
import { PointsHistoryComponent } from "@/components/PointsHistory";
import { PopupVideoComponent } from "@/components/PopupVideo/PopupVideo.builder";
import { PrizeComponent } from "@/components/Prize";
import { ProductCardComponent } from "@/components/ProductCard/ProductCard.builder";
import { ProductCardContainerComponent } from "@/components/ProductCardContainer/ProductCardContainer.builder";
import { PromoCardHorizontalComponent } from "@/components/PromoCardHorizontal";
import { PromoCardVerticalComponent } from "@/components/PromoCardVertical/PromoCardVertical.builder";
import { PromotionComponent } from "@/components/Promotion/Promotion.builder";
import { ProvincePopupComponent } from "@/components/ProvincePopup";
import { PushBlockComponent } from "@/components/PushBlock/PushBlock.builder";
import { PushBlockContainerComponent } from "@/components/PushBlockContainer/PushBlockContainer.builder";
import { QrAgeVerificationContainerComponent } from "@/components/QrAgeVerificationContainer/QrAgeVerificationContainer.builder";
import { QrCodeLotteryComponent } from "@/components/QrCodeLottery";
import { QrCodePointsCollectionComponent } from "@/components/QrCodePointsCollection";
import { QuickLinksComponent } from "@/components/QuickLinks/QuickLinks.builder";
import { ReassuranceBannerComponent } from "@/components/ReassuranceBanner";
import { ReferAFriendComponent } from "@/components/ReferAFriend";
import { ReferralHistoryComponent } from "@/components/ReferralHistory";
import { ReferralPersonalCodeComponent } from "@/components/ReferralPersonalCode";
import { ReferralStatsComponent } from "@/components/ReferralStats";
import { ResponsiveImageComponent } from "@/components/ResponsiveImage/ResponsiveImage.builder";
import { RichTextComponent } from "@/components/RichText/RichText.builder";
import { RowComponent } from "@/components/Row/Row.builder";
import { ScrollDepthTriggerComponent } from "@/components/ScrollDepthTrigger/ScrollDepthTrigger.builder";
import { SearchBoxComponent } from "@/components/SearchBox";
import { SearchResultsComponent } from "@/components/SearchResults";
import { ImageNavLinksListComponent } from "@/components/SecondaryNavigation/components/ImageNavLinksList";
import { InnerContainerComponent } from "@/components/SecondaryNavigation/components/InnerContainer/InnerContainer.builder";
import { ListComponent } from "@/components/SecondaryNavigation/components/List/List.builder";
import { ShopTeaserComponent } from "@/components/SecondaryNavigation/components/ShopTeaser/ShopTeaser.builder";
import { SimpleProductTeaserComponent } from "@/components/SecondaryNavigation/components/SimpleProductTeaser/SimpleProductTeaser.builder";
import { SubmenuTitleComponent } from "@/components/SecondaryNavigation/components/SubmenuTitle/SubmenuTitle.builder";
import { SecondaryNavigationComponent } from "@/components/SecondaryNavigation/SecondaryNavigation.builder";
import { SectionComponent } from "@/components/Section/Section.builder";
import { ShopLandingPageComponent } from "@/components/ShopLandingPage";
import { SimplifiedPrizesComponent } from "@/components/SimplifiedPrizes";
import { SimplifiedPrizesV2Component } from "@/components/SimplifiedPrizesV2";
import { SmartSurveyPushBlockComponent } from "@/components/SmartSurveyPushBlock";
import { SobQuizComponent } from "@/components/SobQuiz/SobQuiz.builder";
import { SocialSharingIconsComponent } from "@/components/SocialSharingIcons/SocialSharingIcons.builder";
import { SoftAgeVerificationComponent } from "@/components/SoftAgeVerification";
import { StickyContainerComponent } from "@/components/StickyContainer/StickyContainer.builder";
import { StoreDetailsComponent } from "@/components/StoreDetails";
import { StoreLocatorComponent } from "@/components/StoreLocator";
import { SurveyComponent } from "@/components/Survey/Survey.builder";
import { SurveyMonkeyComponent } from "@/components/SurveyMonkey";
import { TableComponent } from "@/components/Table/Table.builder";
import { TelephoneComponent } from "@/components/Telephone/Telephone.builder";
import { TiersBenefitsComponent } from "@/components/TiersBenefits";
import { TileComponent } from "@/components/Tile";
import { TrialHeroBannerComponent } from "@/components/TrialHeroBanner/TrialHeroBanner.builder";
import { TrustedPlacesComponent } from "@/components/TrustedPlaces";
import { TrustedPlacesV2Component } from "@/components/TrustedPlacesV2";
import { TrustPilotMicroComboComponent } from "@/components/TrustPilotMicroCombo";
import { TrustPilotMicroReviewCountComponent } from "@/components/TrustPilotMicroReviewCount";
import { TrustPilotReviewsWidgetComponent } from "@/components/TrustPilotReviewsWidget";
import { TrustPilotStarsComponent } from "@/components/TrustPilotStars";
import { UnsubscribeBlockComponent } from "@/components/UnsubscribeBlock/UnsubscribeBlock.builder";
import { UserAuthComponent } from "@/components/UserAuth/UserAuth.builder";
import { UserProfileComponent } from "@/components/UserProfile/UserProfile.builder";
import { VideoContainerComponent } from "@/components/VideoContainer/VideoContainer.builder";
import { VideoPlayerComponent } from "@/components/VideoPlayer";
import { WelcomePopupComponent } from "@/components/WelcomePopup";
import { XSellingPopupComponent } from "@/components/XSellingPopup";

export const commonCustomComponents: RegisteredComponent[] = [
  AccordionComponent,
  AccordionItemComponent,
  AccountDashboardBannerComponent,
  AdditionalBenefitsComponent,
  AnchorLinkComponent,
  AnchorPushComponent,
  ArticleRestrictedContentComponent,
  BlogArticleCarouselComponent,
  BlogArticleComponent,
  BlogComponent,
  BuilderImageComponent,
  CallToActionComponent,
  CartComponent,
  CheckoutComponent,
  ColumnComponent,
  ContactBlockComponent,
  ContactFormComponent,
  ContainerComponent,
  CtaComponent,
  CtaBlockComponent,
  DoubleOptInRedirectComponent,
  DownloadButtonComponent,
  EarnPointsComponent,
  EcommerceReassuranceBannerComponent,
  EventsLocatorComponent,
  FaqArticleComponent,
  FlavourCarouselCardComponent,
  FlavourCarouselComponent,
  FaqResultsContainerComponent,
  FlavorFinderComponent,
  FormCheckboxComponent,
  FormConsentCheckboxListComponent,
  FormSelectComponent,
  FormTextAreaComponent,
  FormTextInputComponent,
  HeaderBlockComponent,
  HeroComponent,
  HighlightCardComponent,
  ImageInfoBannerComponent,
  ImageLinkComponent,
  ImageTopicGalleryComponent,
  ImageTopicGalleryTopicComponent,
  InfoBanner,
  InnerContainerComponent,
  ProductCardContainerComponent,
  LegalDisclaimerComponent,
  LinksListComponent,
  ListComponent,
  LogoComponent,
  LoyaltyHeroComponent,
  LoyaltyHeroBannerContainerComponent,
  LoyaltyTabsComponent,
  LoyaltyWrapperComponent,
  MiscellaneousPageContainerComponent,
  NotFoundComponent,
  OnsitePersonalizationModalComponent,
  OverviewComponent,
  PackCodesComponent,
  PersonalizationContainerComponent,
  PointsAmountBannerComponent,
  PointsHistoryComponent,
  ProvincePopupComponent,
  PushBlockComponent,
  PushBlockContainerComponent,
  QrCodeLotteryComponent,
  QrCodePointsCollectionComponent,
  QuickLinksComponent,
  ReassuranceBannerComponent,
  ReferAFriendComponent,
  ReferralHistoryComponent,
  ReferralPersonalCodeComponent,
  ReferralStatsComponent,
  RichTextComponent,
  RowComponent,
  SearchBoxComponent,
  SearchResultsComponent,
  SecondaryNavigationComponent,
  SectionComponent,
  ShopLandingPageComponent,
  ShopTeaserComponent,
  SimpleProductTeaserComponent,
  SimplifiedPrizesComponent,
  SimplifiedPrizesV2Component,
  SobQuizComponent,
  SocialSharingIconsComponent,
  SmartSurveyPushBlockComponent,
  SoftAgeVerificationComponent,
  StoreDetailsComponent,
  StoreLocatorComponent,
  HoReCaLocatorComponent,
  SubmenuTitleComponent,
  SurveyComponent,
  SurveyMonkeyComponent,
  TableComponent,
  TelephoneComponent,
  TiersBenefitsComponent,
  LoyaltyVideoPlayerComponent,
  LoyaltyVideoPreviewComponent,
  TileComponent,
  TrustedPlacesComponent,
  TrustedPlacesV2Component,
  TrustPilotMicroComboComponent,
  TrustPilotMicroReviewCountComponent,
  TrustPilotReviewsWidgetComponent,
  TrustPilotStarsComponent,
  UnsubscribeBlockComponent,
  UserAuthComponent,
  UserProfileComponent,
  VideoPlayerComponent,
  WelcomePopupComponent,
  SearchBoxComponent,
  CheckoutComponent,
  ImageTopicGalleryComponent,
  ImageTopicGalleryTopicComponent,
  HighlightCardComponent,
  ProductCardComponent,
  ContentGateComponent,
  ConsentReminderDialogComponent,
  ImageNavLinksListComponent,
  HealthBannerComponent,
  HeroBannerContainerComponent,
  HeroContainerComponent,
  VideoContainerComponent,
  FooterListComponent,
  QrAgeVerificationContainerComponent,
  HeroBannerWithCardsComponent,
  StickyContainerComponent,
  GridComponent,
  TrialHeroBannerComponent,
  ComingSoonComponent,
  InformationAboutProductsComponent,
  GenericLayoutTitleComponent,
  PrizeComponent,
  DiscountComponent,
  CountrySelectorComponent,
  LoyaltyReferralStatsComponent,
  LoyaltyReferralCodeExtendedComponent,
  PromoCardHorizontalComponent,
  PromoCardVerticalComponent,
  PromotionComponent,
  PromoCardHorizontalComponent,
  PromoCardVerticalComponent,
  LoyaltyPointsStatusComponent,
  PopupVideoComponent,
  ScrollDepthTriggerComponent,
  FaqComponent,
  CounterComponent,
  ResponsiveImageComponent,
  LoyaltyContactFormComponent,
  LoyaltyContactFormTextAreaComponent,
  LoyaltyContactFormCheckboxSectionComponent,
  DiscreteHeaderBannerComponent,
  XSellingPopupComponent,
  LoyaltyPushBlockComponent,
  LoyaltySoftGateComponent,
];
